import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, ArrowRight, Sparkles, Info } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PartnerOfTheMonth
 * -----------------
 * Homepage spotlight card that surfaces the highest-performing
 * affiliate partner for the current calendar month. Lives directly
 * under `<AllFeaturedPartnersStrip />` (the moving banner) and above
 * the EcosystemHowItWorks explainer.
 *
 * Selection logic happens server-side at GET /api/partner-of-the-month
 * — this component is purely presentational. Per locked spec (Feb
 * 2026): NO numbers shown publicly. Marketing copy only.
 *
 * Failure modes:
 *   - API down / pending request   → renders a tiny skeleton
 *   - Endpoint returns null winner → renders nothing (no empty card)
 */
export default function PartnerOfTheMonth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/partner-of-the-month`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        // Silent — if the spotlight can't render, the homepage just
        // skips it rather than showing a broken card.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section
        className="container mx-auto px-4 md:px-12 max-w-5xl pt-6 md:pt-8"
        aria-hidden
      >
        <div
          className="h-32 md:h-28 rounded-2xl border border-white/10 bg-white/[0.02] animate-pulse"
          data-testid="partner-of-month-skeleton"
        />
      </section>
    );
  }

  if (!data?.partner) return null;

  const p = data.partner;
  // Use the tracked /go/ redirect so this counts in click analytics
  // alongside every other affiliate touchpoint.
  const goUrl = `${API_URL}${p.go_path}`;
  const monthLabel = formatMonth(data.month);

  return (
    <section
      className="container mx-auto px-4 md:px-12 max-w-5xl pt-8 md:pt-10"
      data-testid="partner-of-the-month-section"
      aria-labelledby="potm-heading"
    >
      <article
        className="group relative overflow-hidden rounded-2xl border border-[#00F0FF]/25 bg-gradient-to-br from-[#00F0FF]/[0.08] via-white/[0.03] to-transparent p-5 md:p-6"
        data-testid="partner-of-month-card"
      >
        {/* Decorative glow blob behind the trophy */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-12 -right-12 h-44 w-44 rounded-full bg-[#00F0FF]/[0.15] blur-3xl"
        />

        <div className="relative flex items-center gap-4 md:gap-6 flex-wrap md:flex-nowrap">
          {/* Logo container — matches the dark-container pattern used
              across the site (AllFeaturedPartnersStrip uses bg-neutral-900/60,
              FeaturedDirectory cards use bg-black/60). All partner SVGs
              were authored to read on a dark surface — using a white
              container here breaks that convention and any white-fill
              brand mark (Emergent) becomes invisible. */}
          <div className="flex-shrink-0">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-neutral-900/70 border border-white/10 p-3 flex items-center justify-center shadow-lg shadow-black/30">
              {p.logo_url ? (
                <img
                  src={p.logo_url}
                  alt={p.name}
                  className="max-w-full max-h-full object-contain"
                  data-testid="partner-of-month-logo"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <Sparkles className="h-8 w-8 text-[#00F0FF]" />
              )}
            </div>
          </div>

          {/* Copy block */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <Trophy className="h-3.5 w-3.5 text-[#00F0FF]" strokeWidth={2.5} />
              <span
                id="potm-heading"
                className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-bold text-[#00F0FF]"
              >
                Top Featured Partner · {monthLabel}
              </span>
              {/* "How this works" hint — signals merit-based, not paid
                  placement. Click/tap to open (works on mobile too —
                  hover tooltips disappear too fast on touch devices).
                  Closes by tapping outside or hitting the icon again. */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="How is the Top Featured Partner picked?"
                    data-testid="partner-of-month-info"
                    className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[#00F0FF]/60 hover:text-[#00F0FF] hover:bg-[#00F0FF]/10 focus-visible:text-[#00F0FF] focus-visible:bg-[#00F0FF]/10 transition cursor-pointer"
                  >
                    <Info className="h-3.5 w-3.5" strokeWidth={2.25} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="start"
                  sideOffset={8}
                  className="max-w-[280px] bg-neutral-950 text-white border border-white/15 px-4 py-3 text-xs leading-relaxed font-normal normal-case tracking-normal shadow-xl"
                >
                  Updated automatically based on platform performance.
                  New winner picked at the start of every month as earned.
                </PopoverContent>
              </Popover>
            </div>
            <h3
              className="text-xl md:text-2xl font-bold text-white leading-tight truncate"
              data-testid="partner-of-month-name"
            >
              {p.name}
            </h3>
            {p.tagline && (
              <p
                className="mt-1 text-sm md:text-base text-white/65 leading-snug line-clamp-2"
                data-testid="partner-of-month-tagline"
              >
                {p.tagline}
              </p>
            )}
          </div>

          {/* CTA */}
          <div className="flex-shrink-0 w-full md:w-auto">
            <a
              href={goUrl}
              target="_blank"
              rel="noopener noreferrer sponsored"
              data-testid="partner-of-month-cta"
              className="group/cta inline-flex items-center justify-center gap-1.5 w-full md:w-auto px-5 py-2.5 rounded-full bg-[#00F0FF] text-black font-bold text-sm hover:bg-[#00F0FF]/90 transition shadow-[0_0_20px_rgba(0,240,255,0.25)]"
            >
              Try {p.name}
              <ArrowRight className="h-4 w-4 transition-transform group-hover/cta:translate-x-0.5" />
            </a>
          </div>
        </div>
      </article>
    </section>
  );
}

/**
 * "2026-05" → "May 2026". Defensive — bad input falls back to ''.
 */
function formatMonth(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const idx = parseInt(m, 10) - 1;
  const names = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  if (idx < 0 || idx > 11 || !y) return '';
  return `${names[idx]} ${y}`;
}
